var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g = Object.create((typeof Iterator === "function" ? Iterator : Object).prototype);
    return g.next = verb(0), g["throw"] = verb(1), g["return"] = verb(2), typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { fetchSystemDataAsync, getCssAndApply } from '../../shared/ThemeableProvider';
import { defaultTheme } from '../../shared/ThemeableProvider/defaultTheme';
import { ViewportSizeProvider } from './ProviderResponsive';
export var ThemeableContext = createContext(undefined);
export var ThemeableProvider = function (_a) {
    var systemId = _a.systemId, _b = _a.svgPath, svgPath = _b === void 0 ? './icons.svg' : _b, colorMode = _a.colorMode, children = _a.children;
    var _c = useState(defaultTheme), systemData = _c[0], setSystemData = _c[1];
    var _d = useState(false), isLoaded = _d[0], setIsLoaded = _d[1];
    var _e = useState('compact'), browserViewport = _e[0], setBrowserViewport = _e[1];
    var _f = useState(window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'), detectedColorMode = _f[0], setDetectedColorMode = _f[1];
    useEffect(function () {
        if (colorMode === undefined) { // Only auto-detect if no override
            var darkThemeMQ_1 = window.matchMedia('(prefers-color-scheme: dark)');
            var handleThemeChange_1 = function (event) {
                setDetectedColorMode(event.matches ? 'dark' : 'light');
            };
            darkThemeMQ_1.addEventListener('change', handleThemeChange_1);
            return function () { return darkThemeMQ_1.removeEventListener('change', handleThemeChange_1); };
        }
    }, [colorMode]);
    var prefResponsive = systemData.foundations.size.responsive;
    useEffect(function () {
        var updateViewportSize = function () {
            var width = window.innerWidth;
            if (width > prefResponsive.expanded) {
                setBrowserViewport('expanded');
            }
            else if (width > prefResponsive.medium) {
                setBrowserViewport('medium');
            }
            else {
                setBrowserViewport('compact');
            }
        };
        updateViewportSize();
        window.addEventListener('resize', updateViewportSize);
        return function () { return window.removeEventListener('resize', updateViewportSize); };
    }, [prefResponsive]);
    // Memoize the fetch function itself
    var fetchData = useMemo(function () {
        return function () { return __awaiter(void 0, void 0, void 0, function () {
            var data;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        if (!systemId)
                            return [2 /*return*/];
                        return [4 /*yield*/, fetchSystemDataAsync(systemId)];
                    case 1:
                        data = _a.sent();
                        setSystemData(data);
                        return [4 /*yield*/, getCssAndApply(data.css)];
                    case 2:
                        _a.sent();
                        setIsLoaded(true);
                        return [2 /*return*/];
                }
            });
        }); };
    }, [systemId]); // Only re-creates function if systemId changes
    useEffect(function () {
        fetchData().catch(function (error) { return console.error('Error fetching data:', error); });
    }, [fetchData]);
    // Resolve the effective color mode: use prop if provided, else fall back to detected
    var effectiveColorMode = colorMode || detectedColorMode;
    var contextValue = {
        systemData: systemData,
        browserViewport: browserViewport,
        colorMode: effectiveColorMode, // Add colorMode to context
        svgPath: svgPath,
    };
    return (_jsx(ThemeableContext.Provider, { value: contextValue, children: _jsx(ViewportSizeProvider, { setMaxViewSize: "expanded", children: _jsx("div", { style: {
                    visibility: isLoaded ? 'visible' : 'hidden',
                    opacity: isLoaded ? 1 : 0,
                    height: '100vh',
                }, children: children }) }) }));
};
export var useThemeableSystemData = function () {
    var context = useContext(ThemeableContext);
    if (!context) {
        throw new Error('useThemeableSystemData must be used within a ThemeableProvider');
    }
    return context;
};
